/* Crochets natifs (personnalisations Ava Be).

   Ce module est le seul point de contact entre le thème et l'application
   iOS construite avec WebViewGold. Il ne fait rien du tout dans un
   navigateur ordinaire : tout est conditionné à la détection du contexte
   application, et chaque appel natif est une simple redirection vers un
   schéma d'URL propriétaire (spinneroff://, registerpush://, …) que seul
   le conteneur natif comprend.

   POURQUOI CE FICHIER EXISTE. Une application WebView n'est pas une
   application native, mais elle peut lui ressembler beaucoup si le site
   sait qu'il tourne dedans. Quatre choses valent le détour :

     1. couper l'indicateur de chargement natif dès que la page est
        lisible, au lieu d'attendre la fin du chargement de Trustpilot et
        des scripts tiers — c'est ce qui donne l'impression de vitesse ;
     2. bloquer les versions trop anciennes de l'application quand une
        évolution du site n'est plus compatible ;
     3. demander l'autorisation de notification au bon moment, pas au
        premier lancement ;
     4. récupérer l'identifiant d'abonnement OneSignal pour pouvoir un
        jour cibler les envois.

   CE QU'IL NE FAUT PAS FAIRE ICI. Ne jamais appeler un schéma natif sans
   avoir vérifié qu'on est bien dans l'application : dans Safari, une
   redirection vers « registerpush:// » affiche une alerte d'erreur au
   visiteur. Toute la logique passe donc par natif(), qui refuse de
   travailler hors application.

   RÉFÉRENCE DES APPELS UTILISÉS (documentation WebViewGold pour iOS) :
     spinneron://  spinneroff://        indicateur de chargement
     getappversion://                   injecte versionNumber, bundleNumber
     registerpush://                    demande l'autorisation de notification
     checkNativePushPermissions://      injecte nativePushEnabled
     getonesignalplayerid://            injecte onesignalplayerid

   POINT VÉRIFIÉ, ET IMPORTANT. Le Cloud Builder n'expose PAS toutes les
   options du fichier Config.swift décrit dans cette documentation. En
   particulier « autoInjectVariable », qui injecterait versionNumber et
   onesignalplayerid tout seuls, n'y figure pas : la recherche dans
   l'interface ne remonte que « Auto Inject Safe Area », qui est autre
   chose. On ne peut donc pas compter dessus.

   Conséquence sur la conception : la détection repose sur un jeton ajouté
   au UserAgent (champ « Custom iOS UserAgent », lui bien présent dans le
   Cloud Builder), et les deux variables sont demandées explicitement par
   getappversion:// et getonesignalplayerid:// — ce qui est sans risque
   puisqu'on ne les appelle qu'après avoir reconnu l'application. */

(() => {
  const source = document.getElementById('avb-app-data');
  if (!source) return;

  const settings = source.dataset;
  const root = document.documentElement;

  /* ------------------------------------------------------------------
     Petits utilitaires
     ------------------------------------------------------------------ */

  /* Le stockage local lève une exception en navigation privée sur
     certaines versions d'iOS. On l'enveloppe une fois pour toutes plutôt
     que d'éparpiller des try/catch. */
  const memo = {
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch {
        /* rien : la fonctionnalité se dégrade, elle ne casse pas */
      }
    },
  };

  /* Un appel natif est une navigation. On la fait dans un iframe jetable
     plutôt que sur window.location : sur window.location, deux appels
     rapprochés s'annulent, et un schéma inconnu peut interrompre la page
     en cours. L'iframe isole l'effet de bord. */
  const natif = (schema) => {
    if (!contexte.application) return;

    const cadre = document.createElement('iframe');
    cadre.style.display = 'none';
    cadre.src = schema;
    document.body.appendChild(cadre);
    window.setTimeout(() => cadre.remove(), 200);
  };

  /* ------------------------------------------------------------------
     Détection du contexte
     ------------------------------------------------------------------ */

  const contexte = { application: false, version: null };

  /* Le jeton dans le UserAgent est le seul signal disponible avant tout
     appel natif, et c'est bien ce qu'il faut : il est synchrone, donc
     aucune fenêtre de temps pendant laquelle on ignorerait où l'on se
     trouve, et surtout aucun appel natif hasardeux dans un navigateur.

     Le champ correspondant s'appelle « Custom iOS UserAgent » dans le
     Cloud Builder. Tant qu'il n'est pas renseigné, tout ce module reste
     inerte — c'est le comportement voulu : mieux vaut une application
     qui se comporte comme le site qu'un site qui déclenche des alertes
     système chez les visiteurs du navigateur.

     La mémoire ne sert qu'à éviter un clignotement : si le UserAgent
     répond, elle est superflue ; elle rattrape le cas d'un UserAgent
     temporairement absent. */
  const jeton = settings.userAgentToken || '';

  const reconnaitre = () => {
    if (contexte.application) return true;
    if (jeton === '' || !navigator.userAgent.includes(jeton)) return false;

    contexte.application = true;
    root.classList.add('avb-app');
    memo.set('avb-app', '1');

    return true;
  };

  if (jeton !== '' && memo.get('avb-app') === '1') {
    contexte.application = true;
    root.classList.add('avb-app');
  }

  reconnaitre();

  /* ------------------------------------------------------------------
     Bouton retour

     L'application n'a ni barre d'adresse ni flèche de navigation :
     revenir en arrière suppose de connaître le balayage depuis le bord,
     que rien n'enseigne. Le thème rend une flèche dans l'en-tête,
     cachée ; elle n'est révélée qu'ici, donc jamais dans un navigateur.

     ELLE N'APPARAÎT QUE S'IL Y A OÙ REVENIR. Sur le premier écran,
     une flèche inerte apprendrait qu'elle ne sert à rien, et la
     confiance perdue ne se regagne pas. `history.length` est le seul
     témoin disponible ; il n'est pas parfait — il compte aussi les
     navigations avant — mais son seul défaut possible est d'afficher
     une flèche qui ramène au même écran, jamais d'en cacher une utile.

     L'en-tête est re-rendu par l'éditeur de thème et certaines
     navigations : on re-révèle à chaque apparition plutôt qu'une fois. */
  const revelerRetour = () => {
    if (!contexte.application) return;
    if (window.history.length <= 1) return;

    document.querySelectorAll('[data-avb-app-retour]').forEach((bouton) => {
      bouton.hidden = false;
    });
  };

  document.addEventListener('click', (event) => {
    const bouton = event.target.closest('[data-avb-app-retour]');
    if (bouton) window.history.back();
  });

  revelerRetour();

  if ('MutationObserver' in window) {
    new MutationObserver(revelerRetour).observe(root, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------
     Langue de l'appareil, au premier lancement

     Le site sert le français par défaut : une cliente italienne qui
     installe l'application arrivait sur une boutique en français, sans
     savoir qu'il existe treize langues derrière le sélecteur.

     AU PREMIER LANCEMENT SEULEMENT : la langue du téléphone est lue,
     et si elle fait partie des langues publiées, l'application bascule
     dessus. Le drapeau en mémoire garantit qu'on ne le fait qu'une
     fois : un choix manuel ultérieur ne sera jamais écrasé.

     DANS L'APPLICATION SEULEMENT, à dessein : sur le web, la même
     redirection s'appliquerait au robot de Google et parasiterait
     l'indexation de la page française. Le web garde son sélecteur
     manuel, et le référencement multilingue passe par hreflang.

     La liste des locales est celle publiée sur la boutique ; pt est
     publié sous pt-PT, d'où l'entrée dédiée. Une langue absente de la
     liste (chinois, coréen…) ne redirige nulle part : le français par
     défaut vaut mieux qu'une langue devinée de travers. */
  const appliquerLangueAppareil = () => {
    if (!contexte.application) return;
    if (memo.get('avb-app-langue') === '1') return;
    memo.set('avb-app-langue', '1');

    const publiees = ['fr', 'en', 'de', 'es', 'it', 'nl', 'da', 'fi', 'no', 'ro', 'ru', 'sv', 'ar'];
    const brute = (navigator.language || '').toLowerCase();
    const courte = brute.split('-')[0];

    let locale = null;
    /* pt-PT est publié mais servi à l'adresse /pt — vérifié sur la
       boutique, /pt-pt renvoie une page introuvable. */
    if (brute === 'pt-br' || courte === 'pt') locale = 'pt';
    else if (courte === 'nb' || courte === 'nn') locale = 'no';
    else if (publiees.includes(courte)) locale = courte;

    if (!locale || locale === 'fr') return;

    /* Déjà sur la bonne langue (lien profond, notification) : rien à
       faire. */
    const chemin = window.location.pathname;
    if (chemin === '/' + locale || chemin.startsWith('/' + locale + '/')) return;
    if (chemin.toLowerCase().startsWith('/' + locale.toLowerCase() + '/')) return;

    window.location.replace('/' + locale + chemin + window.location.search);
  };

  appliquerLangueAppareil();

  /* La version de l'application n'est pas injectée d'office : on la
     demande. L'appel est sans danger ici, on sait déjà qu'on est dans
     l'application. La réponse arrive de façon asynchrone dans
     window.versionNumber, d'où la surveillance courte. */
  const demanderVersion = () => {
    if (!contexte.application) return;

    natif('getappversion://');

    let restant = 20;
    const veille = window.setInterval(() => {
      restant -= 1;

      if (typeof window.versionNumber === 'string' && window.versionNumber !== '') {
        contexte.version = window.versionNumber;
        root.dataset.avbAppVersion = contexte.version;
        window.clearInterval(veille);
        return;
      }

      if (restant <= 0) window.clearInterval(veille);
    }, 100);
  };

  /* Le corps de page doit exister pour que natif() puisse y greffer son
     iframe. Sur un script de module, c'est presque toujours le cas ;
     l'attente couvre le cas contraire. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', demanderVersion, { once: true });
  } else {
    demanderVersion();
  }

  /* ------------------------------------------------------------------
     1. Indicateur de chargement
     ------------------------------------------------------------------ */

  /* WebViewGold masque son indicateur quand la page a fini de charger —
     y compris les scripts tiers. Sur nos pages collection, cela veut dire
     attendre Trustpilot et les balises de mesure alors que les produits
     sont déjà affichés. On coupe donc l'indicateur dès que le document
     est interactif : le gain perçu est de l'ordre de la seconde.

     Le pendant, c'est qu'on devient responsable de l'indicateur pour les
     chargements partiels (« Charger + », filtres). D'où spinner(). */
  let compteurSpinner = 0;
  let garde = null;

  const spinner = (actif) => {
    if (!contexte.application) return;

    if (actif) {
      compteurSpinner += 1;
      if (compteurSpinner === 1) natif('spinneron://');

      /* Garde-fou : si une requête ne se termine jamais, l'indicateur
         natif resterait affiché indéfiniment et l'application aurait
         l'air plantée. Au bout de huit secondes on le coupe d'office. */
      window.clearTimeout(garde);
      garde = window.setTimeout(() => {
        compteurSpinner = 0;
        natif('spinneroff://');
      }, 8000);
      return;
    }

    compteurSpinner = Math.max(0, compteurSpinner - 1);
    if (compteurSpinner === 0) {
      window.clearTimeout(garde);
      natif('spinneroff://');
    }
  };

  const couperAuPremierAffichage = () => {
    if (!contexte.application) return;
    natif('spinneroff://');
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', couperAuPremierAffichage, { once: true });
  } else {
    couperAuPremierAffichage();
  }

  /* ------------------------------------------------------------------
     2. Version minimale
     ------------------------------------------------------------------ */

  /* Comparaison de deux numéros du type 1.2.0. On compare segment par
     segment en nombres, jamais en texte : « 1.10.0 » est supérieur à
     « 1.9.0 » alors que l'ordre alphabétique dirait l'inverse. */
  const comparerVersions = (a, b) => {
    const gauche = String(a).split('.').map((n) => parseInt(n, 10) || 0);
    const droite = String(b).split('.').map((n) => parseInt(n, 10) || 0);
    const taille = Math.max(gauche.length, droite.length);

    for (let i = 0; i < taille; i += 1) {
      const x = gauche[i] || 0;
      const y = droite[i] || 0;
      if (x !== y) return x < y ? -1 : 1;
    }

    return 0;
  };

  const verifierVersion = () => {
    const minimum = settings.minVersion || '';

    /* Champ vide = contrôle désactivé. C'est l'état par défaut : tant
       qu'aucune version minimale n'est déclarée dans le snippet, aucun
       utilisateur ne peut se retrouver bloqué par erreur. */
    if (!contexte.application || minimum === '' || !contexte.version) return;
    if (comparerVersions(contexte.version, minimum) >= 0) return;

    const ecran = document.getElementById('avb-app-update');
    if (!ecran) return;

    /* Sans adresse App Store renseignée, on affiche le message sans
       bouton : mieux vaut une explication qu'un bouton qui ne mène nulle
       part. */
    const bouton = ecran.querySelector('[data-avb-app-store]');
    const adresse = settings.storeUrl || '';
    if (bouton) {
      if (adresse === '') bouton.remove();
      else bouton.setAttribute('href', adresse);
    }

    ecran.hidden = false;
    root.classList.add('avb-app-blocked');
  };

  /* La version arrive au plus tard deux secondes après la demande. On
     vérifie deux fois plutôt qu'une : une version qui arriverait en
     retard ne doit pas faire manquer le blocage. */
  window.setTimeout(verifierVersion, 2500);
  window.addEventListener('load', () => window.setTimeout(verifierVersion, 2500));

  /* ------------------------------------------------------------------
     3. Notifications : demande différée
     ------------------------------------------------------------------ */

  /* iOS ne laisse demander l'autorisation qu'UNE SEULE FOIS. Si la
     visiteuse refuse, il n'y a plus aucun moyen de reposer la question
     depuis l'application — il faut passer par les Réglages du téléphone,
     ce que presque personne ne fait. Un refus est donc définitif.

     D'où la mécanique en deux temps :

       — une carte discrète, en HTML, expliquant ce qu'elle recevra ;
       — la vraie demande système, déclenchée uniquement si elle accepte.

     Un « Plus tard » sur notre carte ne coûte rien : on pourra reposer la
     question à la visite suivante. C'est tout l'intérêt de ne pas laisser
     WebViewGold demander au premier lancement, où la réponse est presque
     toujours non.

     Le moment choisi : deuxième journée d'utilisation au moins, et au
     moins trois fiches produit vues. On s'adresse ainsi à quelqu'un qui
     revient et qui cherche, pas à quelqu'un qui vient d'ouvrir. */

  const CLES = {
    demande: 'avb-app-push-demande',
    jours: 'avb-app-jours',
    fiches: 'avb-app-fiches',
    report: 'avb-app-push-report',
  };

  const aujourdhui = new Date().toISOString().slice(0, 10);

  const compterVisite = () => {
    if (!contexte.application) return;

    const jours = (memo.get(CLES.jours) || '').split(',').filter(Boolean);
    if (!jours.includes(aujourdhui)) {
      jours.push(aujourdhui);
      memo.set(CLES.jours, jours.slice(-10).join(','));
    }

    if (settings.pageType === 'product') {
      const vues = parseInt(memo.get(CLES.fiches) || '0', 10) + 1;
      memo.set(CLES.fiches, String(vues));
    }
  };

  const momentOpportun = () => {
    if (memo.get(CLES.demande) === '1') return false;

    /* Report : après un « Plus tard », on laisse passer sept jours. */
    const report = memo.get(CLES.report);
    if (report && Date.now() < parseInt(report, 10)) return false;

    const jours = (memo.get(CLES.jours) || '').split(',').filter(Boolean).length;
    const fiches = parseInt(memo.get(CLES.fiches) || '0', 10);

    return jours >= 2 && fiches >= 3;
  };

  const demanderAutorisation = () => {
    memo.set(CLES.demande, '1');
    natif('registerpush://');
  };

  const carte = document.getElementById('avb-app-push');

  const proposer = () => {
    if (!carte || !contexte.application) return;

    carte.hidden = false;
    /* Le décalage laisse le temps au navigateur de peindre l'état caché
       avant de lancer la transition — sans lui, la carte apparaît d'un
       coup au lieu de monter. */
    window.requestAnimationFrame(() => carte.classList.add('is-visible'));
  };

  const fermer = () => {
    if (!carte) return;
    carte.classList.remove('is-visible');
    window.setTimeout(() => {
      carte.hidden = true;
    }, 300);
  };

  carte?.querySelector('[data-avb-push-accept]')?.addEventListener('click', () => {
    fermer();
    demanderAutorisation();
  });

  carte?.querySelector('[data-avb-push-decline]')?.addEventListener('click', () => {
    fermer();
    memo.set(CLES.report, String(Date.now() + 7 * 24 * 60 * 60 * 1000));
  });

  /* Déclencheur manuel : n'importe quel élément portant
     data-avb-push-optin (un bouton dans le pied de page, un lien dans le
     compte client) ouvre directement la demande système. Utile pour
     rattraper les personnes qui ont dit « Plus tard ». */
  document.addEventListener('click', (event) => {
    const declencheur = event.target.closest('[data-avb-push-optin]');
    if (!declencheur || !contexte.application) return;
    event.preventDefault();
    demanderAutorisation();
  });

  /* On ne propose rien si l'autorisation est déjà accordée. La réponse
     de checkNativePushPermissions:// arrive dans nativePushEnabled, de
     façon asynchrone : on interroge, puis on lit un peu plus tard. */
  const evaluer = () => {
    if (!contexte.application || !momentOpportun()) return;

    natif('checkNativePushPermissions://');

    window.setTimeout(() => {
      if (window.nativePushEnabled === true) {
        memo.set(CLES.demande, '1');
        return;
      }
      proposer();
    }, 800);
  };

  /* ------------------------------------------------------------------
     4. Identifiant OneSignal
     ------------------------------------------------------------------ */

  /* Le but est de pouvoir un jour rattacher un abonnement push à une
     cliente (« ta taille est de retour »). Tant qu'aucun traitement
     serveur n'existe, on se contente de déposer l'identifiant dans un
     cookie : il sera lisible plus tard, sans avoir à modifier
     l'application. Aucune donnée personnelle là-dedans, c'est un
     identifiant d'appareil généré par OneSignal. */
  const memoriserAbonnement = () => {
    if (!contexte.application) return;

    natif('getonesignalplayerid://');

    window.setTimeout(() => {
      const identifiant = window.onesignalplayerid;
      if (typeof identifiant !== 'string' || identifiant === '') return;

      const anLimite = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
      document.cookie = `avb_push_id=${encodeURIComponent(identifiant)}; expires=${anLimite}; path=/; SameSite=Lax`;
    }, 800);
  };

  /* Après la demande de version, pour ne pas empiler deux appels natifs
     dans la même image d'affichage. */
  window.setTimeout(memoriserAbonnement, 4000);

  /* ------------------------------------------------------------------
     Mise en route
     ------------------------------------------------------------------ */

  window.setTimeout(() => {
    compterVisite();
    evaluer();
  }, 2500);

  /* Surface publique minimale : avb-navigation.js s'en sert pour piloter
     l'indicateur pendant « Charger + ». */
  window.avbApp = {
    get isApp() {
      return contexte.application;
    },
    get version() {
      return contexte.version;
    },
    spinner,
    demanderAutorisation,
  };
})();
